import React, { useState } from "react";
import { X } from "lucide-react";
import { LOC_WHATSAPP_MESSAGE_TYPES, defaultLocWhatsappType } from "@/lib/locWhatsapp";

const inputCls =
  "w-full border border-[#e2e1eb] rounded-md px-2.5 py-1.5 text-[13px] focus:outline-none focus:border-yamaha-400";

const toLocalDatetimeValue = (d = new Date()) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Phase 2 — standalone "Mark WhatsApp Sent" logger, for messages already
 *  sent outside this flow (or sent earlier and just being logged now).
 *  Can be submitted any number of times for the same message type. */
export const LocMarkWhatsappSentDialog = ({ caseObj, onClose, onSubmit }) => {
  const [messageType, setMessageType] = useState(() => defaultLocWhatsappType(caseObj));
  const [mobile, setMobile] = useState(caseObj?.mobile_number || "");
  const [sentAt, setSentAt] = useState(() => toLocalDatetimeValue());
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await onSubmit({ message_type: messageType, mobile_number: mobile, sent_at: sentAt, remarks });
      onClose();
    } catch (e) {
      // Error toast already shown by the caller; keep the dialog open to retry.
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="loc-mark-wa-sent-modal">
      <div className="bg-white rounded-xl border border-[#e2e1eb] w-full max-w-sm shadow-xl">
        <div className="px-5 py-3 border-b border-[#e2e1eb] flex items-center justify-between">
          <h3 className="text-[14px] font-semibold text-[#1a1b22]">Mark WhatsApp Sent</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-[#747684]" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">Message Type</label>
            <select data-testid="loc-mark-wa-sent-type" value={messageType} onChange={(e) => setMessageType(e.target.value)} className={inputCls}>
              {LOC_WHATSAPP_MESSAGE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">Mobile Number</label>
            <input data-testid="loc-mark-wa-sent-mobile" value={mobile} onChange={(e) => setMobile(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">Date &amp; Time</label>
            <input type="datetime-local" data-testid="loc-mark-wa-sent-datetime" value={sentAt} onChange={(e) => setSentAt(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">Remarks</label>
            <textarea data-testid="loc-mark-wa-sent-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} className={inputCls} />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-[#e2e1eb] flex justify-end gap-2 bg-[#fafafd] rounded-b-xl">
          <button onClick={onClose} className="px-3 py-1.5 rounded-md text-[13px] font-medium text-[#444653] hover:bg-[#f0eff7]">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="loc-mark-wa-sent-submit-btn"
            className="px-4 py-1.5 rounded-md text-[13px] font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-60">
            {saving ? "Saving…" : "Mark WhatsApp Sent"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocMarkWhatsappSentDialog;
